/* Tiny toast host + imperative toast() — matches the SPA styling and
   timing: slide-in, fade at 3800 ms, removed at 4200 ms. Stacked
   top-right. Scoped under .architect-scope in architect.css. */

import React, { useEffect, useState } from 'react';

export type ToastType = 'info' | 'ok' | 'err';

interface ToastItem {
  id: number;
  msg: string;
  type: ToastType;
}

let counter = 0;
const listeners = new Set<(t: ToastItem) => void>();

export function toast(msg: string, type: ToastType = 'info'): void {
  const item: ToastItem = { id: ++counter, msg, type };
  listeners.forEach((l) => l(item));
}

function ToastRow({ item, onDone }: { item: ToastItem; onDone: (id: number) => void }) {
  const [fading, setFading] = useState(false);
  useEffect(() => {
    const fade = setTimeout(() => setFading(true), 3800);
    const done = setTimeout(() => onDone(item.id), 4200);
    return () => {
      clearTimeout(fade);
      clearTimeout(done);
    };
  }, [item.id, onDone]);
  return (
    <div
      className={'arch-toast ' + item.type}
      style={fading ? { opacity: 0, transition: 'opacity .3s' } : undefined}
    >
      {item.msg}
    </div>
  );
}

export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>([]);
  useEffect(() => {
    const l = (t: ToastItem) => setItems((xs) => [...xs, t]);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  const remove = React.useCallback(
    (id: number) => setItems((xs) => xs.filter((x) => x.id !== id)),
    [],
  );
  return (
    <div className="arch-toast-container" aria-live="polite">
      {items.map((it) => (
        <ToastRow key={it.id} item={it} onDone={remove} />
      ))}
    </div>
  );
}
